// Merging validated segments into trips: update an existing booking when the confirmation
// matches (schedule changes, re-forwards), mark cancellations, otherwise attach to the trip
// whose dates overlap or start a new trip. An email older than the one a booking was last
// updated from only fills gaps. Pure: returns what to write.

import type { Segment, SegmentChange, Trip } from '../types.ts';
import type { ExtractedSegment, ExtractionMethod } from './extracted.ts';
import { addDays, formatClock, formatShortDate } from '../time.ts';
import { newId } from '../text.ts';
import { destinationOf, home, tripName } from '../stops.ts';
import { withStayZone } from '../stayZones.ts';

export interface MergeInput {
  trips: Trip[];
  segments: Segment[];
  incoming: ExtractedSegment[];
  emailId: string;
  method: ExtractionMethod | 'manual';
  now: string;
  /** Airports/cities treated as "home" (flights there don't name a trip). */
  homeCodes?: string[];
  homeCities?: string[];
  /** When the email was sent ("YYYY-MM-DDTHH:mm"); undefined for bookings typed by hand. */
  sentAt?: string;
}

/** History note for an email older than a booking's last update. Never notified. */
export const OLDER_EMAIL = 'Older email';

export interface MergeResult {
  trips: Trip[]; // created or updated
  segments: Segment[]; // created or updated
  changes: SegmentChange[];
  /** Human summary per incoming segment ("Added flight JFK → LIS", "Updated ..."). */
  summary: string[];
}

function sameKey(a: Segment, b: ExtractedSegment): boolean {
  if (a.type !== b.type) return false;
  const confA = a.confirmation?.toUpperCase();
  const confB = b.confirmation?.toUpperCase();
  if (a.type === 'flight') {
    // A booking code covers several legs: match the leg by flight number, else by route.
    if (confA && confB && confA === confB) {
      if (a.number && b.number) return normalizeFlight(a.number) === normalizeFlight(b.number);
      return a.from?.code === b.from?.code && a.to?.code === b.to?.code;
    }
    // No shared code: same flight number on the same date is the same flight.
    if (a.number && b.number) return normalizeFlight(a.number) === normalizeFlight(b.number) && a.start.date === b.start.date;
    // Neither code nor number on one side: same route on the same date.
    return !!a.from?.code && a.from.code === b.from?.code && a.to?.code === b.to?.code && a.start.date === b.start.date;
  }
  if (confA && confB) return confA === confB;
  // Without codes: same place and same start date.
  const pa = (a.place?.name ?? a.title).toLowerCase();
  const pb = (b.place?.name ?? b.title ?? '').toLowerCase();
  return !!pb && pa === pb && a.start.date === b.start.date;
}

function normalizeFlight(n: string): string {
  return n.replace(/\s+/g, '').toUpperCase();
}

function isOlder(existing: Segment, sentAt: string | undefined): boolean {
  return !!sentAt && !!existing.source?.sentAt && sentAt < existing.source.sentAt;
}

/** The booking's source after an update from an email sent at `sentAt` (if known). */
function latestSource(existing: Segment, sentAt: string | undefined): Segment['source'] {
  return sentAt ? { ...existing.source, sentAt } : existing.source;
}

/** What an older email may still add: things the booking doesn't have yet. Returns labels. */
function fillMissing(s: Segment, inc: ExtractedSegment): string[] {
  const filled: string[] = [];
  if (!s.confirmation && inc.confirmation) {
    s.confirmation = inc.confirmation;
    filled.push('code');
  }
  if (!s.number && inc.number) {
    s.number = inc.number;
    filled.push('number');
  }
  if (!s.provider && inc.provider) {
    s.provider = inc.provider;
    filled.push(s.type === 'flight' ? 'airline' : 'provider');
  }
  if (!s.seats?.length && inc.seats?.length) {
    s.seats = inc.seats;
    filled.push('seats');
  }
  if (!s.travelers?.length && inc.travelers?.length) {
    s.travelers = inc.travelers;
    filled.push('travelers');
  }
  if (!s.fare && inc.fare) {
    s.fare = inc.fare;
    filled.push('fare');
  }
  if (!s.price && inc.price) {
    s.price = inc.price;
    filled.push('price');
  }
  if (!s.bags && inc.bags) {
    s.bags = inc.bags;
    filled.push('bags');
  }
  const extra = Object.entries(inc.details ?? {}).filter(([k]) => !(k in s.details));
  if (extra.length) {
    s.details = { ...s.details, ...Object.fromEntries(extra) };
    filled.push(...extra.map(([k]) => k.toLowerCase()));
  }
  return filled;
}

/** Plain-language differences that matter to a traveler. */
export function describeChanges(before: Segment, after: ExtractedSegment): string[] {
  const out: string[] = [];
  const t = (x?: string) => (x ? formatClock(x) : '—');
  if (before.start.date !== after.start.date) out.push(`Date ${formatShortDate(before.start.date)} → ${formatShortDate(after.start.date)}`);
  if (after.start.time && before.start.time !== after.start.time) {
    const label = before.type === 'flight' || before.type === 'train' ? 'Departure' : before.type === 'stay' ? 'Check-in' : 'Time';
    out.push(`${label} ${t(before.start.time)} → ${t(after.start.time)}`);
  }
  if (after.end) {
    if (before.end?.date && before.end.date !== after.end.date) {
      const label = before.type === 'stay' ? 'Check-out' : 'Arrival date';
      out.push(`${label} ${formatShortDate(before.end.date)} → ${formatShortDate(after.end.date)}`);
    }
    if (after.end.time && before.end?.time !== after.end.time) {
      const label = before.type === 'flight' || before.type === 'train' ? 'Arrival' : before.type === 'stay' ? 'Check-out time' : 'End';
      out.push(`${label} ${t(before.end?.time)} → ${t(after.end.time)}`);
    }
  }
  if (after.seats?.length && (before.seats ?? []).join(',') !== after.seats.join(',')) out.push(`Seats ${after.seats.join(', ')}`);
  if (after.from?.terminal && before.from?.terminal !== after.from.terminal) out.push(`Terminal ${after.from.terminal}`);
  return out;
}

function segmentEndDate(s: { start: { date: string }; end?: { date: string } }): string {
  return s.end?.date && s.end.date > s.start.date ? s.end.date : s.start.date;
}

export function mergeIntoTrips(input: MergeInput): MergeResult {
  const h = home(input.homeCodes, input.homeCities);
  const trips = input.trips.map((t) => ({ ...t }));
  const segments = input.segments.map((s) => ({ ...s }));
  const touchedTrips = new Set<string>();
  const touchedSegments = new Set<string>();
  // Trips that got a new booking: their automatic name may change even if their dates don't.
  const gainedBookings = new Set<string>();
  const changes: SegmentChange[] = [];
  const summary: string[] = [];

  for (const inc of input.incoming) {
    const existing = segments.find((s) => sameKey(s, inc));

    // An email sent before the one this booking was last updated from (say, the original
    // confirmation after a schedule change) can only add what the booking is missing.
    if (existing && isOlder(existing, input.sentAt)) {
      const filled = inc.cancelled ? [] : fillMissing(existing, inc);
      if (filled.length) {
        existing.updatedAt = input.now;
        touchedSegments.add(existing.id);
      }
      const note = filled.length ? `${OLDER_EMAIL}; filled in ${filled.join(', ')}` : `${OLDER_EMAIL}; nothing changed`;
      changes.push({ id: newId('chg_'), segmentId: existing.id, at: input.now, emailId: input.emailId, summary: note });
      summary.push(`${note}: ${existing.title}`);
      continue;
    }

    if (existing && inc.cancelled) {
      if (existing.status !== 'cancelled') {
        existing.status = 'cancelled';
        existing.source = latestSource(existing, input.sentAt);
        existing.updatedAt = input.now;
        touchedSegments.add(existing.id);
        changes.push({ id: newId('chg_'), segmentId: existing.id, at: input.now, emailId: input.emailId, summary: 'Cancelled' });
        summary.push(`Cancelled ${existing.title}`);
      }
      continue;
    }
    if (!existing && inc.cancelled) {
      summary.push(`Cancellation for ${inc.confirmation ?? inc.number ?? 'a booking'} didn't match anything; ignored`);
      continue;
    }

    if (existing) {
      const diffs = describeChanges(existing, inc);
      Object.assign(existing, {
        start: { ...existing.start, ...inc.start },
        end: inc.end ? { ...existing.end, ...inc.end } : existing.end,
        from: inc.from ? { ...existing.from, ...inc.from } : existing.from,
        to: inc.to ? { ...existing.to, ...inc.to } : existing.to,
        place: inc.place ? { ...existing.place, ...inc.place } : existing.place,
        seats: inc.seats ?? existing.seats,
        fare: inc.fare ?? existing.fare,
        price: inc.price ?? existing.price,
        bags: inc.bags ?? existing.bags,
        travelers: inc.travelers?.length ? inc.travelers : existing.travelers,
        provider: inc.provider ?? existing.provider,
        number: inc.number ?? existing.number,
        confirmation: existing.confirmation ?? inc.confirmation,
        details: { ...existing.details, ...inc.details },
        status: 'active',
        source: latestSource(existing, input.sentAt),
        updatedAt: input.now,
      } satisfies Partial<Segment>);
      touchedSegments.add(existing.id);
      if (diffs.length) {
        changes.push({ id: newId('chg_'), segmentId: existing.id, at: input.now, emailId: input.emailId, summary: diffs.join('; ') });
        summary.push(`Updated ${existing.title}: ${diffs.join('; ')}`);
      } else {
        summary.push(`No changes to ${existing.title}`);
      }
      extendTrip(trips.find((t) => t.id === existing.tripId), existing, touchedTrips);
      continue;
    }

    // New segment: same booking code as something already on a trip (e.g. the return leg)
    // -> that trip; else a trip whose dates overlap (±1 day); else a new trip.
    const sStart = inc.start.date;
    const sEnd = segmentEndDate(inc);
    const sibling = inc.confirmation
      ? segments.find((s) => s.confirmation?.toUpperCase() === inc.confirmation!.toUpperCase())
      : undefined;
    let trip = sibling
      ? trips.find((t) => t.id === sibling.tripId)
      : trips.find((t) => sStart <= addDays(t.endDate, 1) && sEnd >= addDays(t.startDate, -1));
    if (!trip) {
      const dest = destinationOf(inc, h);
      trip = { id: newId('trip_'), title: dest ?? 'New trip', destination: dest, startDate: sStart, endDate: sEnd };
      trips.push(trip);
      touchedTrips.add(trip.id);
    } else if (!trip.destination || trip.title === 'New trip') {
      const dest = destinationOf(inc, h);
      if (dest) {
        trip.destination = dest;
        trip.title = dest;
        touchedTrips.add(trip.id);
      }
    }
    const seg: Segment = {
      id: newId('seg_'),
      tripId: trip.id,
      type: inc.type,
      title: inc.title ?? inc.type,
      start: inc.start,
      end: inc.end,
      from: inc.from,
      to: inc.to,
      place: inc.place,
      confirmation: inc.confirmation,
      provider: inc.provider,
      number: inc.number,
      travelers: inc.travelers ?? [],
      seats: inc.seats,
      fare: inc.fare,
      price: inc.price,
      bags: inc.bags,
      who: inc.who,
      status: 'active',
      notes: inc.notes,
      details: inc.details ?? {},
      source: { emailId: input.emailId, parsedBy: input.method, sentAt: input.sentAt },
      createdAt: input.now,
      updatedAt: input.now,
    };
    segments.push(seg);
    touchedSegments.add(seg.id);
    changes.push({ id: newId('chg_'), segmentId: seg.id, at: input.now, emailId: input.emailId, summary: 'Added to trip' });
    summary.push(`Added ${seg.title} to ${trip.title}`);
    gainedBookings.add(trip.id);
    extendTrip(trip, seg, touchedTrips);
  }

  // Stays have no airport: their zone comes from a flight landing in their city, or the
  // city table. New and updated stays get it worked out again; others without one pick it
  // up when a flight to their city arrives.
  segments.forEach((s, i) => {
    if (s.type !== 'stay' || (s.start.timeZone && !touchedSegments.has(s.id))) return;
    const zoned = withStayZone(s, segments);
    if (!touchedSegments.has(s.id) && !zoned.start.timeZone) return;
    segments[i] = zoned;
    touchedSegments.add(s.id);
  });

  // Name new and auto-named trips from all their legs at once, so a connection that happened
  // to be processed first doesn't name the trip. A name someone chose is left alone.
  for (const trip of trips) {
    if (!(touchedTrips.has(trip.id) || gainedBookings.has(trip.id))) continue;
    if (trip.title !== 'New trip' && trip.title !== trip.destination) continue;
    const dest = tripName(
      segments.filter((s) => s.tripId === trip.id && s.status !== 'cancelled'),
      h,
    );
    if (dest && dest !== trip.destination) {
      trip.destination = dest;
      trip.title = dest;
      touchedTrips.add(trip.id);
    }
  }

  return {
    trips: trips.filter((t) => touchedTrips.has(t.id)),
    segments: segments.filter((s) => touchedSegments.has(s.id)),
    changes,
    summary,
  };
}

function extendTrip(trip: Trip | undefined, seg: Segment, touched: Set<string>): void {
  if (!trip) return;
  const end = segmentEndDate(seg);
  if (seg.start.date < trip.startDate) {
    trip.startDate = seg.start.date;
    touched.add(trip.id);
  }
  if (end > trip.endDate) {
    trip.endDate = end;
    touched.add(trip.id);
  }
}
